import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Ship, Plane, Truck, ArrowRight, CheckCircle, Clock, Shield, Globe, MapPin, Warehouse, Send, PackageCheck, Wallet, Headphones } from 'lucide-react';
import { AnimatedStat } from './AnimatedStats';
import { InteractiveWorldMap } from './InteractiveWorldMap';
import { getUtm } from '../../lib/utm';
import { trackLeadConversion } from '../../lib/gtag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MapLeadForm = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { toast.error('Вкажіть ім\'я та телефон'); return; }
    if (website) return; // honeypot
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name.trim(), phone: phone.trim(),
        message: 'Заявка з блоку "Про компанію" (карта)',
        source: 'about_map_form', website: '', ...getUtm()
      });
      trackLeadConversion();
      setDone(true);
      toast.success('Заявку відправлено! Ми зателефонуємо найближчим часом.');
    } catch {
      toast.error('Помилка відправки. Спробуйте ще раз.');
    } finally { setSending(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-amber-400/30 bg-[#0a1a3d]/60 p-5 flex items-center gap-3" data-testid="about-lead-form-success">
        <CheckCircle className="w-7 h-7 text-amber-300 shrink-0" />
        <div>
          <p className="text-white font-semibold text-sm">Дякуємо! Заявку прийнято</p>
          <p className="text-amber-300/80 text-xs">Наш логіст зателефонує вам найближчим часом.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-amber-400/30 bg-[#0a1a3d]/60 p-4" data-testid="about-lead-form">
      <p className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
        <Send className="w-4 h-4 text-amber-300" /> Залиште заявку — прорахуємо вартість доставки безкоштовно
      </p>
      <input type="text" value={website} onChange={(e) => setWebsite(e.target.value)}
        style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ваше ім'я"
          className="h-11 bg-slate-900/60 border-amber-400/30 text-white placeholder:text-slate-500 focus:border-amber-400" data-testid="about-lead-name" />
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+380 ..." type="tel"
          className="h-11 bg-slate-900/60 border-amber-400/30 text-white placeholder:text-slate-500 focus:border-amber-400" data-testid="about-lead-phone" />
        <Button type="submit" disabled={sending}
          className="h-11 bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-slate-900 font-bold shadow-[0_0_20px_rgba(245,197,66,0.4)]"
          data-testid="about-lead-submit">
          <Send className="w-4 h-4 mr-1.5" /> {sending ? 'Відправка...' : 'Відправити'}
        </Button>
      </div>
    </form>
  );
};

export const Features = () => {
  const features = [
    { icon: Clock, title: 'Швидка доставка', description: 'Від 7 днів до України авіа', glow: '#3b82f6' },
    { icon: Shield, title: 'Страхування', description: 'Повний захист вантажу', glow: '#22c55e' },
    { icon: Globe, title: 'Відстеження', description: 'Контроль на кожному етапі', glow: '#a855f7' },
    { icon: CheckCircle, title: 'Гарантія', description: 'Повернення при проблемах', glow: '#f97316' },
  ];
  
  return (
    <section className="py-12 bg-[#0a0f1c] border-y border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {features.map((feature, idx) => (
            <div key={idx} className="group flex items-center gap-4 p-4 rounded-xl bg-[#161b22] border border-slate-700/50 hover:border-transparent transition-all duration-300 hover:shadow-[0_0_25px_-8px_var(--glow)]" style={{'--glow': feature.glow}}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_20px_-5px_var(--glow)]" style={{background: `${feature.glow}20`, '--glow': feature.glow}}>
                <feature.icon className="w-6 h-6" style={{color: feature.glow}} />
              </div>
              <div>
                <h3 className="font-semibold text-white">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const USADeliveryBanner = () => {
  return (
    <section className="py-8 bg-gradient-to-r from-blue-900 to-blue-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            {/* USA Flag */}
            <div className="w-16 h-12 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
              <svg viewBox="0 0 60 40" className="w-full h-full">
                <rect width="60" height="40" fill="#B22234"/>
                <rect y="3.08" width="60" height="3.08" fill="white"/>
                <rect y="9.23" width="60" height="3.08" fill="white"/>
                <rect y="15.38" width="60" height="3.08" fill="white"/>
                <rect y="21.54" width="60" height="3.08" fill="white"/>
                <rect y="27.69" width="60" height="3.08" fill="white"/>
                <rect y="33.85" width="60" height="3.08" fill="white"/>
                <rect width="24" height="21.54" fill="#3C3B6E"/>
                <g fill="white" fontSize="2">
                  {[...Array(5)].map((_, row) => (
                    [...Array(6)].map((_, col) => (
                      <circle key={`${row}-${col}`} cx={2 + col * 4} cy={2 + row * 4.3} r="0.8"/>
                    ))
                  ))}
                  {[...Array(4)].map((_, row) => (
                    [...Array(5)].map((_, col) => (
                      <circle key={`alt-${row}-${col}`} cx={4 + col * 4} cy={4.15 + row * 4.3} r="0.8"/>
                    ))
                  ))}
                </g>
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl md:text-2xl font-bold text-white">Доставка з США</h3>
                <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded animate-pulse">NEW</span>
              </div>
              <p className="text-blue-200 text-sm md:text-base">
                Amazon, eBay, люксові бренди — доставимо будь-що з Америки!
              </p>
            </div>
          </div>
          <Link to="/usa">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white font-semibold whitespace-nowrap" data-testid="usa-banner-btn">
              Дізнатись більше
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export const WhyChooseUs = () => {
  const reasons = [
    {
      number: '01',
      title: 'Досвід з 2019 року',
      description: 'Більше 8 років успішної роботи на ринку логістики. Знаємо всі тонкощі доставки з Китаю.'
    },
    {
      number: '02',
      title: 'Прозорі ціни',
      description: 'Ніяких прихованих платежів. Ви завжди знаєте точну вартість доставки до оплати.'
    },
    {
      number: '03',
      title: 'Власні склади',
      description: 'Склади в Китаї та Україні. Консолідуємо вантажі для економії на доставці.'
    },
    {
      number: '04',
      title: 'Страхування вантажу',
      description: 'Повне страхування кожної посилки. Гарантуємо відшкодування при пошкодженні.'
    },
    {
      number: '05',
      title: 'Підтримка 24/7',
      description: 'Менеджери на зв\'язку цілодобово. Відповідаємо на запити протягом години.'
    },
    {
      number: '06',
      title: 'Зручне відстеження',
      description: 'Контролюйте статус посилки в реальному часі через особистий кабінет або на сайті.'
    }
  ];
  
  return (
    <section className="py-20 md:py-28 bg-[#060a13] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05]" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(59,130,246,0.5) 1px, transparent 0)', backgroundSize: '40px 40px'}} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Чому варто обрати нас<span className="text-blue-400">?</span></h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            CargoAgs — це надійний партнер для доставки товарів з Китаю
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, idx) => {
            const glows = ['#3b82f6','#f97316','#a855f7','#22c55e','#0ea5e9','#eab308'];
            const glow = glows[idx % glows.length];
            return (
              <div key={idx} className="group relative bg-[#111827]/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 transition-all duration-500 hover:border-transparent hover:shadow-[0_0_40px_-12px_var(--glow)] overflow-hidden" style={{'--glow': glow}}>
                <div className={`absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500`} style={{background: `linear-gradient(to right, transparent, ${glow}, transparent)`}} />
                <div className="text-5xl font-bold mb-4 transition-colors" style={{color: `${glow}30`}}>
                  {reason.number}
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r" style={{'--tw-gradient-from': glow, '--tw-gradient-to': '#fff'}}>
                  {reason.title}
                </h3>
                <p className="text-slate-400 group-hover:text-slate-300 transition-colors">
                  {reason.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Services = () => {
  const services = [
    {
      icon: Ship,
      title: 'Морська доставка',
      description: 'Економічна доставка морем. Ідеально для великих партій товарів. Термін: 30-45 днів.',
      image: 'https://images.pexels.com/photos/29899416/pexels-photo-29899416.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop'
    },
    {
      icon: Plane,
      title: 'Авіа доставка',
      description: 'Швидка доставка літаком. Оптимально для термінових вантажів. Термін: 7-14 днів.',
      image: 'https://images.unsplash.com/photo-1583911026662-95161686d9a6?w=400&h=300&fit=crop&q=60&fm=webp'
    },
    {
      icon: Truck,
      title: 'Автоперевезення',
      description: 'Доставка автотранспортом до дверей. Вигідно для середніх партій. Термін: 20-30 днів.',
      image: 'https://images.unsplash.com/photo-1761243962976-6ab07278ed98?w=400&h=300&fit=crop&q=60&fm=webp'
    },
  ];
  
  return (
    <section id="services" className="py-20 md:py-28 bg-[#0a0f1c] relative overflow-hidden">
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[120px]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Наші послуги</h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Надаємо повний спектр логістичних послуг для доставки товарів з Китаю в Україну
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, idx) => {
            const glows = ['#0ea5e9','#a855f7','#f97316'];
            const glow = glows[idx];
            return (
              <div key={idx} className="group bg-[#111827]/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden hover:border-transparent hover:shadow-[0_0_40px_-12px_var(--glow)] transition-all duration-500" style={{'--glow': glow}} data-testid={`service-card-${idx}`}>
                <div className="h-48 overflow-hidden relative">
                  <img src={service.image} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-70 group-hover:opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111827] to-transparent" />
                </div>
                <div className="p-6">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_25px_-5px_var(--glow)]" style={{background: `${glow}20`}}>
                    <service.icon className="w-7 h-7" style={{color: glow}} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                  <p className="text-slate-400 group-hover:text-slate-300 transition-colors">{service.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const About = () => (
  <section id="about" className="py-20 md:py-28 bg-[#0a0f1c] relative overflow-hidden">
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px]" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="grid lg:grid-cols-[1fr_1.55fr] gap-8 lg:gap-10 items-center">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Про компанію CargoAgs</h2>
          <p className="text-lg text-slate-300 mb-4 leading-relaxed">
            CargoAgs — провідна українська компанія з доставки товарів з Китаю та США. 
            З 2019 року ми допомагаємо бізнесу та приватним клієнтам отримувати товари 
            швидко, безпечно та за вигідними цінами.
          </p>
          <p className="text-base text-slate-400 mb-4 leading-relaxed">
            Ми пропонуємо повний цикл логістичних послуг: від пошуку та викупу товару на китайських 
            та американських маркетплейсах до доставки прямо до ваших дверей. Наші власні склади 
            в Китаї (Гуанчжоу) та США (Вернон, Коннектикут) забезпечують надійне зберігання 
            та швидку консолідацію вантажів.
          </p>
          <p className="text-base text-slate-400 mb-6 leading-relaxed">
            Працюємо з усіма популярними платформами: 1688, Taobao, Alibaba, Amazon, eBay. 
            Здійснюємо авіа та морську доставку. Прозорі ціни, персональний менеджер та підтримка 24/7.
          </p>
          <div className="stats-impulse-wrap relative grid grid-cols-2 gap-6 p-6 rounded-2xl">
            <div className="stats-impulse-bg" aria-hidden="true">
              <span className="node n1" /><span className="node n2" /><span className="node n3" /><span className="node n4" />
              <span className="pulse p1" /><span className="pulse p2" /><span className="pulse p3" />
            </div>
            <AnimatedStat num="50000+" label="Доставлених посилок" />
            <AnimatedStat num="8+" label="Років на ринку" />
            <AnimatedStat num="99%" label="Задоволених клієнтів" />
            <AnimatedStat num="24/7" label="Підтримка клієнтів" />
          </div>
        </div>
        <div className="relative space-y-4" data-testid="about-world-map">
          {/* Lead form above map */}
          <MapLeadForm />

          {/* Route timings above map */}
          <div className="grid grid-cols-2 gap-4" data-testid="about-routes-bar">
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                <Plane className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Китай → Україна</p>
                <p className="text-amber-300/80 text-xs">Авіа 7–14 днів · Море 30–45 днів</p>
              </div>
            </div>
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
                <Ship className="w-5 h-5 text-amber-300" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm">США → Україна</p>
                <p className="text-amber-300/80 text-xs">Авіа 10–16 днів · Море 25–40 днів</p>
              </div>
            </div>
          </div>

          <InteractiveWorldMap />

          {/* Warehouse network below map */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="about-warehouses-bar">
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Warehouse className="w-4 h-4 text-amber-300" />
                <p className="text-white font-semibold text-sm">Гуанчжоу, Китай</p>
              </div>
              <p className="text-slate-400 text-xs leading-snug">Власний склад: приймання, перевірка якості, фото та консолідація посилок.</p>
            </div>
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Warehouse className="w-4 h-4 text-amber-300" />
                <p className="text-white font-semibold text-sm">Вернон, CT, США</p>
              </div>
              <p className="text-slate-400 text-xs leading-snug">Склад у США: ваш персональний код клієнта та відправлення кожного тижня.</p>
            </div>
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <MapPin className="w-4 h-4 text-amber-300" />
                <p className="text-white font-semibold text-sm">Україна — до дверей</p>
              </div>
              <p className="text-slate-400 text-xs leading-snug">Видача по всій країні: Нова Пошта або кур'єр прямо до ваших дверей.</p>
            </div>
          </div>

          {/* Extra benefits row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="about-benefits-bar">
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <PackageCheck className="w-4 h-4 text-amber-300" />
                <p className="text-white font-semibold text-sm">Викуп під ключ</p>
              </div>
              <p className="text-slate-400 text-xs leading-snug">Викупимо товар на 1688, Taobao, Amazon за вас — оплата у гривні без карток.</p>
            </div>
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Wallet className="w-4 h-4 text-amber-300" />
                <p className="text-white font-semibold text-sm">Прозорі тарифи</p>
              </div>
              <p className="text-slate-400 text-xs leading-snug">Чесна вартість без прихованих комісій. Прорахунок ціни ще до відправлення.</p>
            </div>
            <div className="rounded-2xl border border-amber-400/25 bg-[#0a1a3d]/60 p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Headphones className="w-4 h-4 text-amber-300" />
                <p className="text-white font-semibold text-sm">Особистий менеджер</p>
              </div>
              <p className="text-slate-400 text-xs leading-snug">Персональний логіст на зв'язку 24/7 — підкаже, оформить і супроводить.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Advantages */}
      <div className="mt-20 grid md:grid-cols-3 gap-6">
        {[
          { icon: Globe, title: 'Власні склади', desc: 'Склад у Гуанчжоу (Китай) та Вернон (США). Приймаємо, перевіряємо якість, фотографуємо та консолідуємо ваші замовлення.', glow: '#3b82f6' },
          { icon: Shield, title: 'Гарантія безпеки', desc: 'Страхування вантажів, надійне пакування, повний контроль на кожному етапі. Повне відшкодування у разі пошкодження.', glow: '#22c55e' },
          { icon: Truck, title: 'Доставка по Україні', desc: 'Після прибуття в Україну — доставка Новою Поштою або кур\'єром до дверей. Відправка в день отримання.', glow: '#f97316' },
        ].map((item, i) => (
          <div key={i} className="group bg-[#111827]/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-transparent hover:shadow-[0_0_35px_-10px_var(--glow)] transition-all duration-500" style={{'--glow': item.glow}}>
            <div className="absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity" style={{background: `linear-gradient(to right, transparent, ${item.glow}, transparent)`}} />
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_25px_-5px_var(--glow)]" style={{background: `${item.glow}20`}}>
              <item.icon className="w-7 h-7" style={{color: item.glow}} />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
            <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
